import { useState } from "react";
import { toast } from "sonner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useGetCarrierAliases, useCreateCarrierAlias } from "@/api/providers";
import { Provider } from "@/api.types.v2";
import type { CarrierAlias } from "@/api/providers";
import { useQueryClient } from "@tanstack/react-query";
import {
  MapPin,
  ArrowRight,
  Plus,
  RefreshCw,
  Search,
  Trash2,
} from "lucide-react";

interface CarrierAliasDialogProps {
  open: boolean;
  onClose: () => void;
}

const aliasSchema = z.object({
  provider: z.nativeEnum(Provider),
  alias: z.string().min(1, "Carrier alias is required"),
  canonical: z.string().min(1, "Canonical name is required"),
});

// Common carrier canonical names
const CANONICAL_CARRIERS = [
  "UPS",
  "FEDEX",
  "USPS",
  "DHL",
  "CANADA_POST",
  "PUROLATOR",
  "TNT",
  "ARAMEX",
];

export function CarrierAliasDialog({ open, onClose }: CarrierAliasDialogProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const queryClient = useQueryClient();

  const { data: aliasesData, isLoading } = useGetCarrierAliases();
  const createMutation = useCreateCarrierAlias({
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["carrier-aliases"] });
      toast.success("Alias Created", {
        description: "Carrier alias has been created successfully.",
      });
      form.reset();
    },
    onError: () => {
      toast.error("Failed to create carrier alias.");
    },
  });

  const form = useForm<z.infer<typeof aliasSchema>>({
    resolver: zodResolver(aliasSchema),
    defaultValues: {
      provider: Provider.SHIPSTATION,
      alias: "",
      canonical: "",
    },
  });

  const handleSubmit = (values: z.infer<typeof aliasSchema>) => {
    createMutation.mutate(
      values as { provider: Provider; alias: string; canonical: string }
    );
  };

  // Backend returns Carrier records, not alias mappings
  // We'll need to adapt the data structure
  const filteredAliases =
    aliasesData?.data?.filter((alias: CarrierAlias) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        alias.alias?.toLowerCase().includes(searchLower) ||
        alias.canonical?.toLowerCase().includes(searchLower) ||
        alias.provider?.toLowerCase().includes(searchLower) ||
        alias.id?.toLowerCase().includes(searchLower)
      );
    }) ?? [];

  const getProviderBadgeColor = (provider: Provider) => {
    switch (provider) {
      case Provider.SHIPSTATION:
        return "bg-blue-50 text-blue-700";
      case Provider.CHITCHATS:
        return "bg-green-50 text-green-700";
      case Provider.LOCAL:
        return "bg-gray-50 text-gray-700";
      default:
        return "bg-gray-50 text-gray-700";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Carrier Alias Configuration
          </DialogTitle>
          <DialogDescription>
            Map carrier names from different providers to canonical names for
            consistency.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 flex-1 overflow-hidden">
          {/* Create New Alias Form */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg">Create New Alias</CardTitle>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleSubmit)}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="provider"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Provider</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {Object.values(Provider).map((provider) => (
                              <SelectItem key={provider} value={provider}>
                                {provider}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          The provider that uses this carrier name
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="alias"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Carrier Alias</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="e.g., United Parcel Service"
                            {...field}
                          />
                        </FormControl>
                        <FormDescription>
                          The carrier name as returned by the provider
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="canonical"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Canonical Name</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select canonical name" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CANONICAL_CARRIERS.map((carrier) => (
                              <SelectItem key={carrier} value={carrier}>
                                {carrier}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormDescription>
                          The standardized name for this carrier
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    className="w-full"
                    disabled={createMutation.isPending}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {createMutation.isPending ? "Creating..." : "Create Alias"}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          {/* Existing Aliases Table */}
          <Card className="lg:col-span-2 flex flex-col">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Existing Aliases</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search aliases..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9 w-[200px]"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() =>
                      queryClient.invalidateQueries({
                        queryKey: ["carrier-aliases"],
                      })
                    }
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Provider</TableHead>
                    <TableHead>Alias → Canonical</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8">
                        <div className="flex items-center justify-center">
                          <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : filteredAliases.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={3}
                        className="text-center py-8 text-gray-500"
                      >
                        {searchTerm
                          ? "No aliases match your search"
                          : "No aliases configured"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredAliases.map((alias: CarrierAlias) => (
                      <TableRow key={alias.id}>
                        <TableCell>
                          <Badge
                            className={getProviderBadgeColor(
                              alias.provider ?? alias.id
                            )}
                          >
                            {alias.provider ?? alias.id ?? "Unknown"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {/* Handle both alias structure and Carrier structure */}
                            {alias.alias ? (
                              <>
                                <span className="text-sm text-gray-600">
                                  "{alias.alias}"
                                </span>
                                <ArrowRight className="h-4 w-4 text-gray-400" />
                                <Badge variant="outline">
                                  {alias.canonical}
                                </Badge>
                              </>
                            ) : (
                              <>
                                <span className="text-sm text-gray-600">
                                  {(alias as { code?: string }).code ??
                                    alias.alias ??
                                    "No code"}
                                </span>
                                <ArrowRight className="h-4 w-4 text-gray-400" />
                                <Badge variant="outline">
                                  {(alias as { name?: string }).name ??
                                    alias.canonical ??
                                    "No name"}
                                </Badge>
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-600 hover:text-red-800"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Example Mappings */}
        <div className="border-t pt-4">
          <p className="text-sm text-gray-600 mb-2">Example mappings:</p>
          <div className="flex flex-wrap gap-2">
            <Badge variant="secondary" className="text-xs">
              ShipStation "United Parcel Service" → UPS
            </Badge>
            <Badge variant="secondary" className="text-xs">
              ChitChats "ups" → UPS
            </Badge>
            <Badge variant="secondary" className="text-xs">
              ShipStation "FedEx" → FEDEX
            </Badge>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
